// shared/services/url-parser.service.ts
import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class UrlParserService {
    private static readonly REDIRECT_EVENT_ID_KEY = 'redirectEventId';
    private static readonly REDIRECT_SEGMENT_KEY = 'redirectSegment';

    encodeId(id: string): string {
        try {
            // Convert to base64
            const base64 = btoa(encodeURIComponent(id));
            // Make URL-safe and remove padding
            return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        } catch (error) {
            console.error('Encoding failed:', error);
            return id;
        }
    }

    decodeId(encodedId: string): string {
        try {
            if (!encodedId) return encodedId;
            
            // Restore base64 characters
            let base64 = encodedId.replace(/-/g, '+').replace(/_/g, '/');
            
            // Restore padding (base64 requires length to be multiple of 4)
            while (base64.length % 4 !== 0) {
                base64 += '=';
            }
            
            // Decode
            const decoded = atob(base64);
            const decodedValue = decodeURIComponent(decoded);

            // Only treat the value as encoded if it matches the app's
            // URL-safe base64 format after a round-trip encode.
            if (this.normalizeEncodedValue(this.encodeId(decodedValue)) === this.normalizeEncodedValue(encodedId)) {
                return decodedValue;
            }

            return encodedId;
        } catch (error) {
            console.error('Decoding failed for:', encodedId, error);
            // Return original if decoding fails (for backward compatibility)
            return encodedId;
        }
    }

    private normalizeEncodedValue(value: string): string {
        return value.replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_');
    }

    parseAndStoreEventRedirect(url: string): void {
        try {
            const urlObj = new URL(url, window.location.origin);
            const token = urlObj.searchParams.get('token');
            const segment = urlObj.searchParams.get('segment');

            if (!token) return;

            const pathMatch = urlObj.pathname.match(/\/view\/([a-zA-Z0-9\-_]+)/);
            const encodedEventId = pathMatch?.[1];

            if (encodedEventId) {
                const eventId = this.decodeId(encodedEventId);
                localStorage.setItem(UrlParserService.REDIRECT_EVENT_ID_KEY, eventId);
                
                if (segment) {
                    const decodedSegment = this.decodeId(segment);
                    localStorage.setItem(UrlParserService.REDIRECT_SEGMENT_KEY, decodedSegment);
                }
            }
        } catch (e) {
            console.error('URL parsing error:', e);
        }
    }

    getRedirectEventId(): string | null {
        return localStorage.getItem(UrlParserService.REDIRECT_EVENT_ID_KEY);
    }

    getRedirectSegment(): string | null {
        return localStorage.getItem(UrlParserService.REDIRECT_SEGMENT_KEY);
    }

    clearRedirectData(): void {
        localStorage.removeItem(UrlParserService.REDIRECT_EVENT_ID_KEY);
        localStorage.removeItem(UrlParserService.REDIRECT_SEGMENT_KEY);
    }
}
